"use client";
import Image from "next/image";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";

type RiskCategory = "very low" | "low" | "medium" | "moderate to high" | "high" | "very high";

type RiskAnalysisProps = {
  legends?: boolean;
  riskCategory: RiskCategory;
};

const RISK_META: Record<RiskCategory, { angle: number; color: string; title: string; description: string }> = {
  "very low": {
    angle: -73,
    color: "#30AD43",
    title: "Extremely Low Risk",
    description: "Suitable for conservative investments and investors with low risk tolerance.",
  },
  low: {
    angle: -44,
    color: "#5ECB3B",
    title: "Low Risk",
    description: "Suitable for conservative investments and investors with low risk / low-moderate risk tolerance.",
  },
  medium: {
    angle: -15,
    color: "#F5C02E",
    title: "Medium Risk",
    description: "Suitable for balanced investments and investors with medium risk tolerance.",
  },
  "moderate to high": {
    angle: 13,
    color: "#FF8B3D",
    title: "Moderate to High Risk",
    description: "Suitable for balanced investments and investors with moderately high risk tolerance.",
  },
  high: {
    angle: 43,
    color: "#F95B4E",
    title: "High Risk",
    description: "Suitable for aggressive investments and investors with high-risk tolerance.",
  },
  "very high": {
    angle: 72,
    color: "#E43E3E",
    title: "Very High Risk",
    description: "Suitable for aggressive investments and investors with very high-risk tolerance.",
  },
};

const riskLevels: { label: RiskCategory; color: string }[] = [
  { label: "very low", color: "#30AD43" },
  { label: "low", color: "#84BD32" },
  { label: "medium", color: "#D8B00F" },
  { label: "moderate to high", color: "#FEDB14" },
  { label: "high", color: "#FF8888" },
  { label: "very high", color: "#FF5656" },
];

export default function RiskAnalysisCard({ riskCategory, legends = false }: RiskAnalysisProps) {
  const { angle, color, description } = RISK_META[riskCategory];
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  useEffect(() => {
    if (inView) {
      const animateSequence = async () => {
        await controls.start({ rotate: 75, transition: { duration: 0.6 } });
        await controls.start({ rotate: -75, transition: { duration: 0.2 } });
        await controls.start({ rotate: angle, transition: { duration: 0.8 } });
      };

      animateSequence();
    }
  }, [inView, angle, controls]);

  return (
    <div ref={ref} className="w-full rounded-2xl p-6 bg-white border border-[#E9E9EB]">
      <div className="flex flex-col sm:flex-row sm:items-center gap-10">
        <div className="relative w-full max-w-200 shrink-0 mx-auto sm:ml-6 sm:mr-0">
          <Image src="/next_assets/images/scheme-details/big-risk-meter.png" alt="Risk Meter" width={200} height={130} className="relative" />

          <div className="w-fit absolute left-0 bottom-0 right-0 mx-auto">
            <motion.span className="w-fit absolute left-0 bottom-2.5 right-0 mx-auto origin-bottom" animate={controls} initial={{ rotate: -75 }}>
              <svg width="11" height="81" viewBox="0 0 11 81" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.48815 0L0.735272 72.0161H10.241L5.48815 0Z" fill="#323232" />
              </svg>
            </motion.span>

            <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7.47906" cy="7.47906" r="8.31941" transform="matrix(-1 0 0 1 16.9581 2)" fill="#323232" stroke="white" strokeWidth="1.5" />
            </svg>
          </div>
        </div>

        <div className="text-center sm:text-left">
          {/* <p className="text-2xs sm:text-base font-semibold text-primaryBlack">{title}</p> */}
          <div className="text-base sm:text-2xl font-bold mt-2" style={{ color }}>
            {riskCategory.toUpperCase()} RISK
          </div>
          <p className="text-2xs sm:text-base text-primaryBlack mt-2">{description}</p>
        </div>
      </div>
      {legends && (
        <div className="flex items-center sm:justify-between sm:gap-4 flex-wrap sm:flex-nowrap sm:overflow-x-auto mt-6">
          {riskLevels.map((level) => (
            <div key={level.label} className={"w-[45%] sm:w-auto flex gap-2 items-center xs:justify-center px-3 py-2 shrink-0 text-xs sm:text-sm text-[#444444] relative"}>
              <span className="w-3 h-3 rounded-full inline-block shrink-0" style={{ backgroundColor: level.color }} />
              <span className="capitalize whitespace-nowrap">{level.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
